import { useQuery } from '@tanstack/vue-query';
import type { BLMView, AttributeBLMParameter } from '@/api-v2/web/blm';
import { createBlmView, deleteBlmView, getBlmViews, updateBlmView } from '@/api-v2/web/blm';
import type { LoadingWhenEnabledQRT, VQQueryOptions } from '@/types/VueQueryTypes';
import { defineMutation } from './defineMutation';
import useLoadingWhenEnabledQuery from './useLoadingWhenEnabledQuery';
import { chainEnabled } from '@/utils/queries';

const keys = {
  all: () => [{ scope: 'blm-view' }] as const,
  list: () => [{ ...keys.all()[0], entity: 'list' }] as const,
};

const select = {
  blmViews: (data: BLMView[]): BLMView[] => {
    return [...data].sort((a, b) => a.name.localeCompare(b.name));
  },
};

/**
 * Get all BLM views
 */
export function useGetBlmViews(options: VQQueryOptions = {}): LoadingWhenEnabledQRT<BLMView[], Error> {
  const enabled = chainEnabled(options.enabled);
  return useLoadingWhenEnabledQuery(
    useQuery<BLMView[], Error>({
      queryKey: keys.list(),
      queryFn: ({ signal }): Promise<BLMView[]> => getBlmViews(signal),
      ...options,
      enabled,
      select: select.blmViews,
    }),
    enabled,
  );
}

/**
 * Create a new BLM view
 */
export const useCreateBlmView = defineMutation({
  mutationFn: ({
    body,
  }: {
    body: {
      name: string;
      parameter: AttributeBLMParameter[];
    };
  }) => createBlmView(body),
  invalidateCache(client) {
    return client.invalidateQueries({ queryKey: keys.all() });
  },
});

/**
 * Update a single BLM view
 */
export const useUpdateBlmView = defineMutation({
  mutationFn: ({ body }: { body: { attributeId: string; view: BLMView } }) => updateBlmView(body),
  invalidateCache(client, data) {
    const selectors = [keys.all(), [{ blmId: data.body.attributeId }]];
    return Promise.all(selectors.map((selector) => client.invalidateQueries({ queryKey: selector })));
  },
});

/**
 * Delete a single BLM view
 */
export const useDeleteBlmView = defineMutation({
  mutationFn: ({ attributeId }: { attributeId: string }) => deleteBlmView({ attributeId }),
  invalidateCache(client, data) {
    const selectors = [keys.all(), [{ blmId: data.attributeId }]];
    return Promise.all(selectors.map((selector) => client.invalidateQueries({ queryKey: selector })));
  },
});
